/**
 * BillType: Defines different billing methods for calculating messaging volume.
 *
 * Used in:
 * - Org.defaultBillType: Specifies the preferred billing method for an org
 * - Billing.volumeSource: Records how volume was actually fetched for a bill
 *
 * Values:
 * - standard: Excludes 'failed' and 'waiting' status (current method)
 * - conservative: Excludes 'failed', 'waiting', and 'sent' status (only delivered/read)
 * - karix: Fetches volume from uploaded Karix CSV data
 */
export enum BillType {
  STANDARD = 'standard',
  CONSERVATIVE = 'conservative',
  KARIX = 'karix',
}

/**
 * Statuses considered billable under each BillType.
 *
 * - Standard: excludes 'failed' and 'waiting' (charges from 'sent' onwards).
 * - Conservative / Karix: excludes 'failed', 'waiting', and 'sent' (charges from 'delivered' onwards).
 */
export const BILLABLE_STATUSES_STANDARD: ReadonlySet<string> = new Set([
  'sent',
  'delivered',
  'read',
  'responded',
  'clicked',
  'clicked_responded',
]);

export const BILLABLE_STATUSES_CONSERVATIVE: ReadonlySet<string> = new Set([
  'delivered',
  'read',
  'responded',
  'clicked',
  'clicked_responded',
]);

export const getBillableStatuses = (billType: BillType): ReadonlySet<string> =>
  billType === BillType.STANDARD
    ? BILLABLE_STATUSES_STANDARD
    : BILLABLE_STATUSES_CONSERVATIVE;
